/**
 * Réponse du microservice utilisateurs à un import Excel. Il répond 200 même quand des
 * lignes échouent : c'est `isCompleted` et `error.notification` qui le disent, une
 * ligne en échec par ligne de `notification`.
 */
export interface ImportUsersResponse {
    isCompleted?: boolean;
    userNotification?: string;
    error?: { notification?: string } | null;
    payload?: { data?: unknown[] } | null;
}

export interface ImportUsersSummary {
    ok: boolean;
    message: string;
}

/**
 * Causes connues, renvoyées en anglais par le microservice, et leur libellé pour
 * l'écran. Une cause absente d'ici s'affiche telle quelle.
 */
const KNOWN_CAUSES: { startsWith: string; label: string }[] = [
    {
        startsWith: 'target product alone grants access to no establishment',
        label: "le droit « Pages produit » seul ne donne accès à aucun établissement : "
            + "ajoutez aussi une ligne poi, campaign, gmb ou gmb_post pour cet utilisateur dans l'onglet des permissions",
    },
    {
        startsWith: 'target history alone grants access to no establishment',
        label: "le droit « Historique des modifications » seul ne donne accès à aucun établissement : "
            + "ajoutez aussi une ligne poi, campaign, gmb ou gmb_post pour cet utilisateur dans l'onglet des permissions",
    },
    {
        startsWith: 'target review_moderation alone grants access to no establishment',
        label: "le droit « Modération des réponses aux avis » seul ne donne accès à aucun établissement : "
            + "ajoutez aussi une ligne poi, campaign, gmb ou gmb_post pour cet utilisateur dans l'onglet des permissions",
    },
];

/**
 * « Unable to create user <adresse> for client <id>[: ]<cause> » devient
 * « <adresse> : <cause> ». Le client est retiré : l'import se fait déjà dans son
 * contexte, et son identifiant ne dit rien à l'utilisateur. Les deux formes du
 * microservice sont lues, avec et sans séparateur avant la cause.
 */
function describeFailure(line: string): string {
    const match = /^Unable to create user (\S+) for client [0-9a-fA-F-]{36}:?\s*([\s\S]*)$/.exec(line);
    if (match == null) {
        return line;
    }
    const [, email, rawCause] = match;
    // Une cause peut tenir sur plusieurs lignes (erreur SQL et sa ligne DETAIL).
    const cause = rawCause.split(/\s+/).join(' ').trim();
    const known = KNOWN_CAUSES.find(candidate => cause.startsWith(candidate.startsWith));
    const label = known?.label ?? cause;
    return label === '' ? `${email} : création impossible` : `${email} : ${label}`;
}

/**
 * Ce que l'écran annonce après un import. Tout créé : un simple compte. Au moins un
 * échec : combien ont été créés, combien ont échoué, et pourquoi, ligne par ligne —
 * « Utilisateurs créés » s'affichait sinon même quand rien ne l'avait été.
 */
export function summarizeImportResponse(response: ImportUsersResponse | null | undefined): ImportUsersSummary {
    const created = response?.payload?.data?.length ?? 0;
    const failures = (response?.error?.notification ?? '')
        // Un échec commence par « Unable to create user » : les lignes qui suivent
        // appartiennent à sa cause, elles ne comptent pas comme des échecs de plus.
        .split(/\n(?=Unable to create user )/)
        .map(line => line.trim())
        .filter(line => line !== '');
    if (response?.isCompleted !== false && failures.length === 0) {
        return { ok: true, message: `Utilisateurs créés : ${created}` };
    }
    const detail = failures.length > 0 ? '\n\n' + failures.map(describeFailure).join('\n') : '';
    return {
        ok: false,
        message: `Import terminé avec des erreurs : ${created} créé(s), ${failures.length} en échec.${detail}`,
    };
}
